import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
    Filter, Grid3X3, List, SlidersHorizontal, X, Search,
    Sun, Building2, ChevronDown, Star, Zap, BadgePercent, Calculator, UserCheck, ArrowRight, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import ProductCard from '../components/ProductCard';
import { API } from '../App';
import { toast } from 'sonner';
import ExpertVisitModal from '../components/ExpertVisitModal';

const ShopPage = () => {
    const { category } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [brands, setBrands] = useState([]);
    const [viewMode, setViewMode] = useState('grid');
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    const [showExpertModal, setShowExpertModal] = useState(false);
    const brandScrollRef = useRef(null);

    const scrollBrands = useCallback((direction) => {
        if (brandScrollRef.current) {
            brandScrollRef.current.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
        }
    }, []);

    // Filter states
    const [filters, setFilters] = useState({
        category: category || searchParams.get('category') || '',
        minPrice: parseInt(searchParams.get('minPrice')) || 0,
        maxPrice: parseInt(searchParams.get('maxPrice')) || 500000,
        minSize: parseFloat(searchParams.get('minSize')) || 0,
        maxSize: parseFloat(searchParams.get('maxSize')) || 100,
        brands: searchParams.get('brands')?.split(',').filter(Boolean) || [],
        minRating: parseInt(searchParams.get('minRating')) || 0,
        inStock: searchParams.get('inStock') === 'true'
    });

    const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'featured');
    const [searchQuery, setSearchQuery] = useState('');
    const [subsidyOnly, setSubsidyOnly] = useState(searchParams.get('subsidy') === 'true');
    const recommendedKw = parseFloat(searchParams.get('recommendedKw')) || null;

    // Local slider state for smooth dragging (committed to filters only on mouse-up)
    const [sliderValues, setSliderValues] = useState({
        size: [parseFloat(searchParams.get('minSize')) || 0, parseFloat(searchParams.get('maxSize')) || 100],
        price: [parseInt(searchParams.get('minPrice')) || 0, parseInt(searchParams.get('maxPrice')) || 500000]
    });

    useEffect(() => {
        fetchProducts();
        fetchBrands();
    }, [filters, sortBy, category, subsidyOnly, searchQuery]);

    useEffect(() => {
        if (category) {
            setFilters(prev => ({ ...prev, category }));
        }
    }, [category]);

    // Dynamic max size based on category
    const getMaxSizeForCategory = (cat) => {
        if (cat === 'home') return 20;
        if (cat === 'commercial') return 100;
        return 100; // all products
    };

    const maxSizeLimit = getMaxSizeForCategory(filters.category);
    const maxPriceLimit = filters.category === 'home' ? 500000 : 5000000;

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.category) params.append('category', filters.category);
            if (filters.minPrice > 0) params.append('min_price', filters.minPrice);
            if (filters.maxPrice < 500000) params.append('max_price', filters.maxPrice);
            if (filters.minSize > 0) params.append('min_size', filters.minSize);
            if (filters.maxSize < 100) params.append('max_size', filters.maxSize);
            if (filters.brands.length > 0) params.append('brand', filters.brands[0]);
            if (filters.inStock) params.append('in_stock', 'true');

            const response = await axios.get(`${API}/api/products?${params.toString()}`);
            let data = response.data;

            // Client-side category filtering
            if (filters.category) {
                data = data.filter(p => p.category === filters.category);
            }

            // Client-side brand filtering
            if (filters.brands && filters.brands.length > 0) {
                data = data.filter(p => filters.brands.includes(p.brand));
            }

            // Client-side minimum rating filtering
            if (filters.minRating > 0) {
                data = data.filter(p => (p.rating || 0) >= filters.minRating);
            }

            // Client-side size filtering (backend doesn't support this)
            if (filters.minSize > 0) {
                data = data.filter(p => (p.system_size_kw || 0) >= filters.minSize);
            }
            if (filters.maxSize < maxSizeLimit) {
                data = data.filter(p => (p.system_size_kw || 0) <= filters.maxSize);
            }

            // Client-side price filtering (backend may not support this)
            if (filters.minPrice > 0) {
                data = data.filter(p => (p.price || 0) >= filters.minPrice);
            }
            if (filters.maxPrice < maxPriceLimit) {
                data = data.filter(p => (p.price || 0) <= filters.maxPrice);
            }

            // Client-side sorting
            if (sortBy === 'price_low') {
                data.sort((a, b) => a.price - b.price);
            } else if (sortBy === 'price_high') {
                data.sort((a, b) => b.price - a.price);
            } else if (sortBy === 'rating') {
                data.sort((a, b) => (b.rating || 0) - (a.rating || 0));
            } else if (sortBy === 'size_low') {
                data.sort((a, b) => a.system_size_kw - b.system_size_kw);
            } else if (sortBy === 'size_high') {
                data.sort((a, b) => b.system_size_kw - a.system_size_kw);
            } else if (recommendedKw || sidebarRecdKw) {
                // Sort by proximity to recommended size (from URL or sidebar calculator)
                const recdKw = recommendedKw || sidebarRecdKw;
                data.sort((a, b) => Math.abs(a.system_size_kw - recdKw) - Math.abs(b.system_size_kw - recdKw));
            }

            // Client-side search
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                data = data.filter(p =>
                    p.name.toLowerCase().includes(query) ||
                    p.description?.toLowerCase().includes(query) ||
                    p.brand?.toLowerCase().includes(query)
                );
            }

            // Filter by subsidy eligibility
            if (subsidyOnly) {
                data = data.filter(p => p.subsidy_eligible === true);
            }

            setProducts(data);
        } catch (error) {
            console.error('Failed to fetch products:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await axios.get(`${API}/api/products`).then(res => [...new Set(res.data.map(p => p.brand))]);
            setBrands(response);
        } catch (error) {
            console.error('Failed to fetch brands:', error);
        }
    };

    const handleBrandToggle = (brand) => {
        setFilters(prev => ({
            ...prev,
            brands: prev.brands.includes(brand)
                ? prev.brands.filter(b => b !== brand)
                : [...prev.brands, brand]
        }));
    };

    const clearFilters = () => {
        const maxSz = getMaxSizeForCategory('');
        const newFilters = {
            category: '',
            minPrice: 0,
            maxPrice: 500000,
            minSize: 0,
            maxSize: maxSz,
            brands: [],
            minRating: 0,
            inStock: false
        };
        setFilters(newFilters);
        setSliderValues({ size: [0, maxSz], price: [0, 500000] });
    };

    const activeFilterCount = [
        filters.category,
        filters.minPrice > 0,
        filters.maxPrice < maxPriceLimit,
        filters.minSize > 0,
        filters.maxSize < maxSizeLimit,
        filters.brands.length > 0,
        filters.minRating > 0,
        filters.inStock
    ].filter(Boolean).length;

    // Mini calculator state
    const [calcBill, setCalcBill] = useState('');
    const [calcCity, setCalcCity] = useState('');
    const [calcType, setCalcType] = useState('home');
    const [calcLoading, setCalcLoading] = useState(false);
    const [calcResult, setCalcResult] = useState(null);
    const [sidebarRecdKw, setSidebarRecdKw] = useState(null); // for proximity sorting

    const handleMiniCalc = async () => {
        if (!calcBill || !calcCity) { toast.error('Enter bill amount and city'); return; }
        setCalcLoading(true);
        let size = Math.ceil(parseFloat(calcBill) / 1000); // fallback estimate
        try {
            const res = await axios.post(`${API}/api/calculator`, {
                monthly_bill: parseFloat(calcBill),
                property_type: calcType,
                city: calcCity,
                backup_required: false
            });
            size = res.data.recommended_size_kw;
            setCalcResult(res.data);
        } catch {
            // Use fallback estimate
            setCalcResult({ recommended_size_kw: size, annual_savings: parseFloat(calcBill) * 10 });
        } finally {
            const minSz = Math.max(0, size - 2);
            const maxSz = Math.min(getMaxSizeForCategory(calcType), size + 3);
            setSidebarRecdKw(size);
            setFilters(prev => ({
                ...prev,
                category: calcType,
                minSize: minSz,
                maxSize: maxSz
            }));
            setSliderValues(prev => ({ ...prev, size: [minSz, maxSz] }));
            setCalcLoading(false);
        }
    };

    // Render filters inline (NOT as a sub-component) to prevent remount/focus loss
    const renderFilters = () => (
        <div className="space-y-6">
            {/* Mini Calculator */}
            <div className="p-4 bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl border border-primary/20 space-y-3">
                <Label className="text-sm font-semibold flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-primary" />
                    Find Your System Size
                </Label>
                <Input
                    type="number"
                    placeholder="Monthly electricity bill (₹)"
                    value={calcBill}
                    onChange={e => setCalcBill(e.target.value)}
                    className="text-sm h-9"
                />
                <Input
                    type="text"
                    placeholder="City (e.g. Delhi)"
                    value={calcCity}
                    onChange={e => setCalcCity(e.target.value)}
                    className="text-sm h-9"
                />
                <Select value={calcType} onValueChange={setCalcType}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="home">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                </Select>
                <Button onClick={handleMiniCalc} disabled={calcLoading} size="sm" className="w-full">
                    {calcLoading ? 'Calculating...' : 'Find My Size'}
                </Button>
                {calcResult && (
                    <div className="text-center p-3 bg-card rounded-lg border">
                        <p className="text-xs text-muted-foreground">Recommended Size</p>
                        <p className="text-2xl font-bold text-primary">{calcResult.recommended_size_kw} kW</p>
                        {calcResult.annual_savings && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                                Save ~₹{calcResult.annual_savings?.toLocaleString()}/yr
                            </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">Filters updated ✓</p>
                    </div>
                )}
            </div>

            {/* PM Surya Ghar Subsidy Toggle */}
            <div className="p-4 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl border border-green-500/20">
                <button
                    onClick={() => setSubsidyOnly(!subsidyOnly)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${subsidyOnly
                        ? 'bg-green-500 text-white'
                        : 'bg-card hover:bg-green-50 dark:hover:bg-green-900/20'
                        }`}
                >
                    <span className="flex items-center gap-2 font-medium">
                        <BadgePercent className="w-5 h-5" />
                        Subsidy Products
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${subsidyOnly ? 'bg-white/20' : 'bg-green-100 text-green-700 dark:bg-green-800 dark:text-green-300'
                        }`}>
                        {subsidyOnly ? 'ON' : 'OFF'}
                    </span>
                </button>
                <p className="text-xs text-muted-foreground mt-2">
                    Show only PM Surya Ghar eligible products
                </p>
            </div>

            {/* Category */}
            <div className="space-y-3">
                <Label className="text-sm font-semibold">Category</Label>
                <div className="space-y-2">
                    <button
                        onClick={() => { setFilters(prev => ({ ...prev, category: '', minSize: 0, maxSize: 100, minPrice: 0, maxPrice: 500000 })); setSliderValues({ size: [0, 100], price: [0, 500000] }); }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${!filters.category ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'}`}
                    >
                        <Sun className="w-5 h-5" />
                        All Products
                    </button>
                    <button
                        onClick={() => { setFilters(prev => ({ ...prev, category: 'home', minSize: 0, maxSize: 20, minPrice: 0, maxPrice: 500000 })); setSliderValues({ size: [0, 20], price: [0, 500000] }); }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${filters.category === 'home' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'}`}
                    >
                        <Sun className="w-5 h-5" />
                        Home Solar <span className="text-xs text-muted-foreground ml-auto">up to 20kW</span>
                    </button>
                    <button
                        onClick={() => { setFilters(prev => ({ ...prev, category: 'commercial', minSize: 0, maxSize: 100, minPrice: 0, maxPrice: 5000000 })); setSliderValues({ size: [0, 100], price: [0, 5000000] }); }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${filters.category === 'commercial' ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'}`}
                    >
                        <Building2 className="w-5 h-5" />
                        Commercial Solar <span className="text-xs text-muted-foreground ml-auto">up to 100kW</span>
                    </button>
                </div>
            </div>

            {/* System Size */}
            <div className="space-y-3">
                <Label className="text-sm font-semibold">System Size (kW)</Label>
                <div className="px-2">
                    <Slider
                        min={0}
                        max={maxSizeLimit}
                        step={1}
                        value={sliderValues.size.map(v => Math.min(v, maxSizeLimit))}
                        onValueChange={([min, max]) => setSliderValues(prev => ({ ...prev, size: [min, max] }))}
                        onValueCommit={([min, max]) => setFilters(prev => ({ ...prev, minSize: min, maxSize: max }))}
                        className="my-4"
                    />
                    <div className="flex justify-between text-sm text-muted-foreground">
                        <span>{sliderValues.size[0]} kW</span>
                        <span>{Math.min(sliderValues.size[1], maxSizeLimit)} kW</span>
                    </div>
                </div>
            </div>

            {/* Price Range */}
            <div className="space-y-3">
                <Label className="text-sm font-semibold">Price Range</Label>
                <div className="px-2">
                    <Slider
                        min={0}
                        max={maxPriceLimit}
                        step={5000}
                        value={sliderValues.price.map(v => Math.min(v, maxPriceLimit))}
                        onValueChange={([min, max]) => setSliderValues(prev => ({ ...prev, price: [min, max] }))}
                        onValueCommit={([min, max]) => setFilters(prev => ({ ...prev, minPrice: min, maxPrice: max }))}
                        className="my-4"
                    />
                    <div className="flex justify-between text-sm text-muted-foreground">
                        <span>₹{sliderValues.price[0].toLocaleString()}</span>
                        <span>₹{Math.min(sliderValues.price[1], maxPriceLimit).toLocaleString()}</span>
                    </div>
                </div>
            </div>

            {/* Brands */}
            <div className="space-y-3">
                <Label className="text-sm font-semibold">Brands</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                    {brands.map((brand) => (
                        <div key={brand} className="flex items-center gap-3">
                            <Checkbox
                                id={brand}
                                checked={filters.brands.includes(brand)}
                                onCheckedChange={() => handleBrandToggle(brand)}
                            />
                            <label htmlFor={brand} className="text-sm cursor-pointer">{brand}</label>
                        </div>
                    ))}
                </div>
            </div>

            {/* Rating */}
            <div className="space-y-3">
                <Label className="text-sm font-semibold">Minimum Rating</Label>
                <div className="space-y-2">
                    {[4, 3, 2, 1].map((rating) => (
                        <button
                            key={rating}
                            onClick={() => setFilters(prev => ({ ...prev, minRating: rating }))}
                            className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors ${filters.minRating === rating ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'}`}
                        >
                            <div className="flex items-center">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className={`w-4 h-4 ${i < rating ? 'fill-primary text-primary' : 'text-muted'}`} />
                                ))}
                            </div>
                            <span className="text-sm">&amp; up</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* In Stock */}
            <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                <Checkbox
                    id="inStock"
                    checked={filters.inStock}
                    onCheckedChange={(checked) => setFilters(prev => ({ ...prev, inStock: checked }))}
                />
                <label htmlFor="inStock" className="text-sm cursor-pointer">In Stock Only</label>
            </div>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
                <Button variant="outline" onClick={clearFilters} className="w-full">
                    Clear All Filters ({activeFilterCount})
                </Button>
            )}
        </div>
    );

    return (
        <div className="min-h-screen pt-0 md:pt-20">
            {/* Hero Banner — hidden on mobile, shown on desktop */}
            <section className="hidden md:flex relative h-72 bg-gradient-to-r from-gray-900 to-gray-800 items-center overflow-hidden">
                <div className="absolute inset-0 opacity-25">
                    <img
                        src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1200"
                        alt="Solar Panels"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-transparent" />
                <div className="container-solar relative z-10 flex items-center justify-between gap-8 w-full">
                    {/* Left: Title */}
                    <div className="text-white">
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-4xl font-bold mb-2"
                        >
                            {filters.category === 'home'
                                ? 'Home Solar Solutions'
                                : filters.category === 'commercial'
                                    ? 'Commercial Solar Solutions'
                                    : 'Solar Products'
                            }
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-white/70 text-lg"
                        >
                            Browse premium solar systems &amp; panels
                        </motion.p>
                    </div>

                    {/* Right: Expert Visit CTA */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="shrink-0 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 max-w-xs text-white"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <UserCheck className="w-5 h-5 text-yellow-400" />
                            <span className="text-xs font-semibold text-yellow-400 uppercase tracking-wider">Free Expert Visit</span>
                        </div>
                        <p className="text-sm text-white/80 mb-4 leading-snug">
                            Don't stress yourself — let our expert come to <em>you</em> and pick the perfect solar system for free.
                        </p>
                        <button
                            onClick={() => setShowExpertModal(true)}
                            className="flex items-center gap-2 btn-primary rounded-xl transition-all duration-200 w-full justify-center py-2 text-sm"
                        >
                            Book a Free Visit <ArrowRight className="w-4 h-4" />
                        </button>
                    </motion.div>
                </div>
            </section>

            {/* Mobile Hero Banner — compact with background image */}
            <div className="md:hidden shop-mobile-banner" style={{ marginTop: '-56px', paddingTop: '56px' }}>
                <div className="shop-mobile-banner-bg">
                    <img
                        src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=800"
                        alt="Solar Panels"
                        className="shop-mobile-banner-img"
                    />
                </div>
                <div className="shop-mobile-banner-overlay" />
                <div className="shop-mobile-banner-content">
                    <h1 className="shop-mobile-title">
                        {filters.category === 'home'
                            ? 'Home Solar'
                            : filters.category === 'commercial'
                                ? 'Commercial Solar'
                                : 'Solar Products'
                        }
                    </h1>
                    <p className="shop-mobile-subtitle">Premium solar systems &amp; panels</p>
                    <button
                        onClick={() => setShowExpertModal(true)}
                        className="mt-3 flex items-center gap-1.5 btn-primary rounded-full px-4 py-2 text-xs"
                    >
                        <UserCheck className="w-3.5 h-3.5" /> Free Expert Visit
                    </button>
                </div>
            </div>

            {/* Brand arrow slider — below banner, above controls */}
            {brands.length > 0 && (
                <div className="bg-background border-b border-border">
                    <div className="container-solar py-3">
                        <div className="relative flex items-center">
                            {/* Left Arrow */}
                            <button
                                onClick={() => scrollBrands('left')}
                                className="absolute left-0 z-10 w-8 h-8 flex items-center justify-center bg-background/90 border border-border rounded-full shadow-md hover:bg-secondary transition-colors -ml-1"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            {/* Scrollable brands */}
                            <div
                                ref={brandScrollRef}
                                className="flex items-center gap-3 overflow-x-auto mx-10 scroll-smooth"
                                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                            >
                                <button
                                    onClick={() => setFilters(prev => ({ ...prev, brands: [] }))}
                                    className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                                        filters.brands.length === 0
                                            ? 'bg-primary text-primary-foreground border-primary shadow-md'
                                            : 'bg-card border-border hover:border-primary/50 hover:bg-primary/5'
                                    }`}
                                >
                                    All Brands
                                </button>
                                {brands.filter(Boolean).map(brand => (
                                    <button
                                        key={brand}
                                        onClick={() => setFilters(prev => ({ ...prev, brands: prev.brands.includes(brand) ? prev.brands.filter(b => b !== brand) : [brand] }))}
                                        className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                                            filters.brands.includes(brand)
                                                ? 'bg-primary text-primary-foreground border-primary shadow-md'
                                                : 'bg-card border-border hover:border-primary/50 hover:bg-primary/5'
                                        }`}
                                    >
                                        <span className="w-2 h-2 rounded-full bg-current opacity-60" />
                                        {brand}
                                    </button>
                                ))}
                            </div>

                            {/* Right Arrow */}
                            <button
                                onClick={() => scrollBrands('right')}
                                className="absolute right-0 z-10 w-8 h-8 flex items-center justify-center bg-background/90 border border-border rounded-full shadow-md hover:bg-secondary transition-colors -mr-1"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Mobile controls — normal background */}
            <div className="md:hidden shop-mobile-controls">
                {/* Category chips */}
                <div className="shop-mobile-chips">
                    <button
                        onClick={() => setFilters(prev => ({ ...prev, category: '' }))}
                        className={`shop-chip ${!filters.category ? 'active' : ''}`}
                    >
                        All
                    </button>
                    <button
                        onClick={() => setFilters(prev => ({ ...prev, category: 'home' }))}
                        className={`shop-chip ${filters.category === 'home' ? 'active' : ''}`}
                    >
                        <Sun className="w-3.5 h-3.5" /> Home
                    </button>
                    <button
                        onClick={() => setFilters(prev => ({ ...prev, category: 'commercial' }))}
                        className={`shop-chip ${filters.category === 'commercial' ? 'active' : ''}`}
                    >
                        <Building2 className="w-3.5 h-3.5" /> Commercial
                    </button>
                    <button
                        onClick={() => setSubsidyOnly(!subsidyOnly)}
                        className={`shop-chip ${subsidyOnly ? 'active subsidy' : ''}`}
                    >
                        <BadgePercent className="w-3.5 h-3.5" /> Subsidy
                    </button>
                </div>

                {/* Search bar */}
                <div className="shop-mobile-search">
                    <Search className="w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search solar products..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="shop-mobile-search-input"
                    />
                </div>
            </div>

            {/* Mobile sticky toolbar */}
            <div className="md:hidden shop-mobile-toolbar">
                <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                    <SheetTrigger asChild>
                        <button className="shop-toolbar-btn">
                            <SlidersHorizontal className="w-4 h-4" />
                            Filters
                            {activeFilterCount > 0 && (
                                <span className="shop-toolbar-badge">{activeFilterCount}</span>
                            )}
                        </button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-80 overflow-y-auto">
                        <SheetHeader>
                            <SheetTitle className="flex items-center gap-2">
                                <Filter className="w-5 h-5" />
                                Filters
                            </SheetTitle>
                        </SheetHeader>
                        <div className="mt-6">
                            {renderFilters()}
                        </div>
                    </SheetContent>
                </Sheet>

                <div className="shop-toolbar-divider" />

                <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="shop-toolbar-sort">
                        <SelectValue placeholder="Sort" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="featured">Featured</SelectItem>
                        <SelectItem value="price_low">Price: Low → High</SelectItem>
                        <SelectItem value="price_high">Price: High → Low</SelectItem>
                        <SelectItem value="rating">Top Rated</SelectItem>
                        <SelectItem value="size_low">Size: Small → Large</SelectItem>
                        <SelectItem value="size_high">Size: Large → Small</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="container-solar py-8">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar - Desktop */}
                    <aside className="hidden lg:block w-72 shrink-0">
                        <div className="sticky top-24 bg-card rounded-2xl border border-border p-6 max-h-[calc(100vh-7rem)] overflow-y-auto">
                            <h2 className="font-semibold text-lg mb-6 flex items-center gap-2">
                                <Filter className="w-5 h-5" />
                                Filters
                            </h2>
                            {renderFilters()}
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="flex-1">
                        {/* Desktop Toolbar — hidden on mobile (replaced by mobile toolbar above) */}
                        <div className="hidden md:flex flex-wrap items-center justify-between gap-4 mb-6">
                            {/* Search */}
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    placeholder="Search products..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pl-10"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                {/* Sort */}
                                <Select value={sortBy} onValueChange={setSortBy}>
                                    <SelectTrigger className="w-40">
                                        <SelectValue placeholder="Sort by" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="featured">Featured</SelectItem>
                                        <SelectItem value="price_low">Price: Low to High</SelectItem>
                                        <SelectItem value="price_high">Price: High to Low</SelectItem>
                                        <SelectItem value="rating">Top Rated</SelectItem>
                                        <SelectItem value="size_low">Size: Low to High</SelectItem>
                                        <SelectItem value="size_high">Size: High to Low</SelectItem>
                                    </SelectContent>
                                </Select>

                                {/* View Mode */}
                                <div className="flex items-center border rounded-lg overflow-hidden">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`}
                                    >
                                        <Grid3X3 className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`p-2 ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'}`}
                                    >
                                        <List className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Force client-side filter logic inside render to guarantee sync */}
                        {(() => {
                            const filteredForRender = products.filter(p => {
                                if (filters.category && p.category !== filters.category) return false;
                                if (filters.brands.length > 0 && !filters.brands.includes(p.brand)) return false;
                                if (filters.minRating > 0 && (p.rating || 0) < filters.minRating) return false;
                                if (filters.minSize > 0 && (p.system_size_kw || 0) < filters.minSize) return false;
                                if (filters.maxSize < maxSizeLimit && (p.system_size_kw || 0) > filters.maxSize) return false;
                                if (filters.minPrice > 0 && (p.price || 0) < filters.minPrice) return false;
                                if (filters.maxPrice < maxPriceLimit && (p.price || 0) > filters.maxPrice) return false;
                                if (filters.inStock && !p.in_stock) return false;
                                return true;
                            });
                            
                            return (
                                <>
                        {/* Active Filters */}
                        {activeFilterCount > 0 && (
                            <div className="flex flex-wrap items-center gap-2 mb-6">
                                <span className="text-sm text-muted-foreground">Active filters:</span>
                                {filters.category && (
                                    <Badge variant="secondary" className="gap-1">
                                        {filters.category}
                                        <X
                                            className="w-3 h-3 cursor-pointer"
                                            onClick={() => setFilters(prev => ({ ...prev, category: '' }))}
                                        />
                                    </Badge>
                                )}
                                {filters.brands.map(brand => (
                                    <Badge key={brand} variant="secondary" className="gap-1">
                                        {brand}
                                        <X
                                            className="w-3 h-3 cursor-pointer"
                                            onClick={() => handleBrandToggle(brand)}
                                        />
                                    </Badge>
                                ))}
                                {(filters.minPrice > 0 || filters.maxPrice < 500000) && (
                                    <Badge variant="secondary" className="gap-1">
                                        ₹{filters.minPrice.toLocaleString()} - ₹{filters.maxPrice.toLocaleString()}
                                        <X
                                            className="w-3 h-3 cursor-pointer"
                                            onClick={() => setFilters(prev => ({ ...prev, minPrice: 0, maxPrice: 500000 }))}
                                        />
                                    </Badge>
                                )}
                            </div>
                        )}

                        {/* Products Count — desktop only */}
                        <p className="hidden md:block text-muted-foreground mb-6">
                            Showing {filteredForRender.length} products
                        </p>

                        {/* Products Grid */}
                        {loading ? (
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="bg-card rounded-2xl border border-border overflow-hidden animate-pulse">
                                        <div className="aspect-square bg-secondary" />
                                        <div className="p-4 space-y-3">
                                            <div className="h-4 bg-secondary rounded w-1/4" />
                                            <div className="h-6 bg-secondary rounded w-3/4" />
                                            <div className="h-4 bg-secondary rounded w-1/2" />
                                            <div className="h-6 bg-secondary rounded w-1/3" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredForRender.length > 0 ? (
                            <div className={viewMode === 'grid'
                                ? 'grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6'
                                : 'space-y-4'
                            }>
                                {filteredForRender.map((product, index) => (
                                    <ProductCard key={product.id} product={product} index={index} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-20">
                                <Sun className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                                <h3 className="text-xl font-semibold mb-2">No products found</h3>
                                <p className="text-muted-foreground mb-6">Try adjusting your filters to find what you're looking for.</p>
                                <Button onClick={clearFilters} variant="outline">Clear All Filters</Button>
                            </div>
                        )}
                        </>
                        );
                        })()}
                    </main>
                </div>
            </div>
            {/* Expert Visit Popup Modal */}
            <ExpertVisitModal
                isOpen={showExpertModal}
                onClose={() => setShowExpertModal(false)}
            />
        </div>
    );
};

export default ShopPage;
